/**
 * React Query hooks for project data fetching and mutations.
 * Provides query hooks for reading project data and mutation hooks
 * for create, update, and archive operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    archiveProject,
    createProject,
    getProject,
    getProjectActivity,
    listArchivedProjects,
    listProjectOpenPrs,
    listProjectRepos,
    listProjects,
    updateProject
} from '@/api/projects';
import { FREQUENT_STALE_TIME, INFREQUENT_STALE_TIME } from '@/modules/query/query-client';
import { queryKeys } from '@/modules/query/query-keys';

import type {
    CreateProjectRequest,
    ProjectDetail,
    UpdateProjectRequest,
} from '@/types/project';

/**
 * Fetches the list of all active projects visible to the current user.
 * Projects are sorted by most recent activity descending.
 */
export function useProjects() {
  return useQuery({
    queryKey: queryKeys.projects.all,
    queryFn: listProjects,
    retry: 1,
  });
}

/**
 * Fetches a single project by key, including linked repositories.
 */
export function useProject(key: string) {
  return useQuery({
    queryKey: queryKeys.projects.detail(key),
    queryFn: () => getProject(key),
    enabled: !!key,
    retry: 1,
  });
}

/**
 * Fetches repositories linked to a project by prefix matching.
 */
export function useProjectRepos(key: string) {
  return useQuery({
    queryKey: queryKeys.projects.repos(key),
    queryFn: () => listProjectRepos(key),
    enabled: !!key,
    staleTime: INFREQUENT_STALE_TIME,
    retry: 1,
  });
}

/**
 * Fetches recent activity entries across all repositories in a project.
 * Returns the most recent 20 entries ordered by timestamp descending.
 */
export function useProjectActivity(key: string) {
  return useQuery({
    queryKey: queryKeys.projects.activity(key),
    queryFn: () => getProjectActivity(key),
    enabled: !!key,
    retry: 1,
  });
}

/**
 * Fetches all OPEN pull requests across the project's repositories, newest first.
 * Merged and closed PRs are excluded. Not split by author.
 */
export function useProjectOpenPrs(key: string) {
  return useQuery({
    queryKey: queryKeys.projects.openPrs(key),
    queryFn: () => listProjectOpenPrs(key),
    enabled: !!key,
    staleTime: FREQUENT_STALE_TIME,
    retry: 1,
  });
}

/**
 * Fetches all archived projects. Intended for admin use.
 */
export function useArchivedProjects() {
  return useQuery({
    queryKey: queryKeys.projects.archived,
    queryFn: listArchivedProjects,
    retry: 1,
  });
}

/**
 * Mutation hook for creating a new project.
 * Invalidates the project list cache on success.
 */
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: CreateProjectRequest) => createProject(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
    },
  });
}

/**
 * Mutation hook for updating an existing project.
 * Invalidates both the project list and the specific project detail cache on success.
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, req }: { key: string; req: UpdateProjectRequest }) =>
      updateProject(key, req),
    onSuccess: (data: ProjectDetail) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.detail(data.projectKey),
      });
    },
  });
}

/**
 * Mutation hook for archiving a project.
 * Invalidates the project list and archived projects cache on success.
 */
export function useArchiveProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => archiveProject(key),
    onSuccess: (_data, key) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.archived });
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.detail(key),
      });
    },
  });
}
